/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 * Organization Management Tab - Uses real game data for HR, TPRM, and Audit
 */

import React, { useMemo } from 'react';
import { CityStats, Grid, BuildingType } from '../../../types';
import { BUILDINGS } from '../../../constants';
import { playUiSfx } from '../../../utils/uiAudio';

interface OrgManagementTabProps {
  stats: CityStats;
  grid: Grid;
  onSetOrgManagementOpen: (type: string | null) => void;
}

const OrgManagementTab: React.FC<OrgManagementTabProps> = ({ stats, grid, onSetOrgManagementOpen }) => {
  // Calculate real metrics from grid
  const orgMetrics = useMemo(() => {
    const counts: Record<string, number> = {};
    let totalBuildings = 0;
    let connectedBuildings = 0;
    let operationalBuildings = 0;
    let complianceBuildings = 0;

    grid.flat().forEach(tile => {
      if (tile.buildingType !== BuildingType.None) {
        totalBuildings++;
        counts[tile.buildingType] = (counts[tile.buildingType] || 0) + 1;
        
        if (tile.isConnected) {
          connectedBuildings++;
          const config = BUILDINGS[tile.buildingType];
          if (config.category === 'Ops') {
            operationalBuildings++;
          }
          if (config.category === 'Governance' || config.category === 'EnhancedCompliance' || config.category === 'Legal') {
            complianceBuildings++;
          }
        }
      }
    });

    // Calculate dynamic employee count based on buildings
    // Each building type contributes different employee counts
    let employeeCount = 0;
    Object.entries(counts).forEach(([type, count]) => {
      const config = BUILDINGS[type as BuildingType];
      if (config) {
        // Base employees per building type
        const baseEmployees: Record<string, number> = {
          'Core': 10,
          'ComplianceMgmt': 5,
          'Monitoring': 4,
          'SecurityAML': 6,
          'SecurityFraud': 5,
          'Sanctions': 4,
          'SecurityIncidents': 5,
          'LegalPrivacy': 4,
          'LegalDisputes': 3,
          'LegalMarketing': 3,
          'OpsFunding': 8,
          'OpsComplaints': 6,
          'OpsVendors': 4,
          'SOC': 12,
          'PenTestLab': 6,
          'GDPROffice': 5,
          'InternalAudit': 4,
        };
        employeeCount += (baseEmployees[type] || 2) * count;
      }
    });

    // Vendor count based on OpsVendors buildings
    const vendorCount = (counts[BuildingType.OpsVendors] || 0) * 3;

    // Audit readiness based on compliance buildings
    const auditLabCount = counts[BuildingType.Monitoring] || 0;
    const internalAuditCount = counts[BuildingType.InternalAudit] || 0;
    const auditReadiness = auditLabCount > 0 || internalAuditCount > 0 ? 'Ready' : 'Setup Required';

    // Training coverage based on security training buildings
    const trainingCount = counts[BuildingType.SecurityTraining] || 0;
    const trainingCoverage = Math.min(100, 60 + (trainingCount * 20) + (complianceBuildings * 5));

    return {
      counts,
      totalBuildings,
      connectedBuildings,
      operationalBuildings,
      complianceBuildings,
      employeeCount,
      vendorCount,
      auditReadiness,
      trainingCoverage
    };
  }, [grid]);

  const managementModules = [
    {
      id: 'hr',
      title: 'HR Management',
      icon: '👥',
      color: 'blue',
      description: 'Manage employees, hiring, compensation, and organizational structure',
      features: ['Employee Directory', 'Hiring Pipeline', 'Performance Reviews', 'Training Programs'],
      metrics: [
        { label: 'Team Size', value: orgMetrics.employeeCount.toString() },
        { label: 'Productivity', value: `${Math.min(100, 70 + orgMetrics.operationalBuildings * 5)}%` },
      ]
    },
    {
      id: 'tprm',
      title: 'Third-Party Risk',
      icon: '🔗',
      color: 'orange',
      description: 'Vendor risk management, due diligence, and contract oversight',
      features: ['Vendor Registry', 'Risk Assessments', 'Contract Management', 'SLA Monitoring'],
      metrics: [
        { label: 'Active Vendors', value: orgMetrics.vendorCount.toString() },
        { label: 'Risk Score', value: orgMetrics.vendorCount > 0 ? 'Monitored' : 'N/A' },
      ]
    },
    {
      id: 'audit',
      title: 'Audit & Compliance',
      icon: '📋',
      color: 'green',
      description: 'Internal audits, regulatory examinations, and compliance checklists',
      features: ['Audit Schedule', 'Finding Tracker', 'Evidence Collection', 'Remediation Plans'],
      metrics: [
        { label: 'Open Findings', value: stats.compliance < 70 ? Math.floor((100 - stats.compliance) / 10).toString() : '0' },
        { label: 'Audit Status', value: orgMetrics.auditReadiness },
      ]
    }
  ];

  const getColorClasses = (color: string) => {
    const colors: Record<string, { bg: string; border: string; text: string; button: string }> = {
      blue: {
        bg: 'bg-blue-900/20',
        border: 'border-blue-700/30',
        text: 'text-blue-400',
        button: 'bg-blue-600 hover:bg-blue-500'
      },
      orange: {
        bg: 'bg-orange-900/20',
        border: 'border-orange-700/30',
        text: 'text-orange-400',
        button: 'bg-orange-600 hover:bg-orange-500'
      },
      green: {
        bg: 'bg-green-900/20',
        border: 'border-green-700/30',
        text: 'text-green-400',
        button: 'bg-green-600 hover:bg-green-500'
      }
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border-b border-white/10 pb-4">
        <h3 className="text-xl font-bold text-white uppercase tracking-wider">Organizational Management</h3>
        <p className="text-slate-400 mt-2">Manage human resources, vendors, and compliance operations</p>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-slate-900/30 border border-white/10 rounded-lg p-4 text-center">
          <div className="text-2xl mb-1">👥</div>
          <div className="text-xs text-slate-400 uppercase">Employees</div>
          <div className="text-lg font-bold text-white">{orgMetrics.employeeCount}</div>
        </div>
        <div className="bg-slate-900/30 border border-white/10 rounded-lg p-4 text-center">
          <div className="text-2xl mb-1">🔗</div>
          <div className="text-xs text-slate-400 uppercase">Vendors</div>
          <div className="text-lg font-bold text-white">{orgMetrics.vendorCount}</div>
        </div>
        <div className="bg-slate-900/30 border border-white/10 rounded-lg p-4 text-center">
          <div className="text-2xl mb-1">📋</div>
          <div className="text-xs text-slate-400 uppercase">Audits</div>
          <div className={`text-lg font-bold ${orgMetrics.auditReadiness === 'Ready' ? 'text-emerald-400' : 'text-yellow-400'}`}>{orgMetrics.auditReadiness}</div>
        </div>
        <div className="bg-slate-900/30 border border-white/10 rounded-lg p-4 text-center">
          <div className="text-2xl mb-1">📊</div>
          <div className="text-xs text-slate-400 uppercase">Compliance</div>
          <div className="text-lg font-bold text-cyan-400">{stats.compliance}%</div>
        </div>
      </div>

      {/* Management Modules */}
      <div className="grid md:grid-cols-3 gap-4">
        {managementModules.map(module => {
          const colors = getColorClasses(module.color);
          return (
            <div 
              key={module.id}
              className={`${colors.bg} border ${colors.border} rounded-lg p-5 flex flex-col`}
            >
              {/* Module Header */}
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-12 h-12 ${colors.button} rounded-lg flex items-center justify-center text-2xl`}>
                  {module.icon}
                </div>
                <div>
                  <h4 className="text-white font-bold">{module.title}</h4>
                  <p className={`text-xs ${colors.text}`}>Click to open dashboard</p>
                </div>
              </div>

              {/* Description */}
              <p className="text-sm text-slate-400 mb-4">{module.description}</p>

              {/* Features */}
              <div className="mb-4 flex-1">
                <div className="text-xs text-slate-500 uppercase font-bold mb-2">Features</div>
                <div className="grid grid-cols-2 gap-1">
                  {module.features.map((feature, idx) => (
                    <div key={idx} className="text-xs text-slate-400 flex items-center gap-1">
                      <span className={colors.text}>•</span> {feature}
                    </div>
                  ))}
                </div>
              </div>

              {/* Metrics */}
              <div className="grid grid-cols-2 gap-2 mb-4">
                {module.metrics.map((metric, idx) => (
                  <div key={idx} className="bg-black/30 rounded p-2 text-center">
                    <div className="text-xs text-slate-500">{metric.label}</div>
                    <div className={`text-sm font-bold ${colors.text}`}>{metric.value}</div>
                  </div>
                ))}
              </div>

              {/* Open Button */}
              <button
                onClick={() => {
                  playUiSfx('click');
                  onSetOrgManagementOpen(module.id);
                }}
                className={`w-full py-3 ${colors.button} text-white rounded-lg font-bold uppercase tracking-wider text-sm transition-all hover:shadow-lg`}
              >
                Open {module.title}
              </button>
            </div>
          );
        })}
      </div>

      {/* Educational Resources */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-slate-900/30 border border-white/10 rounded-lg p-5">
          <h4 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <span>📚</span> Best Practices
          </h4>
          <div className="space-y-3">
            <div className="p-3 bg-white/5 rounded">
              <h5 className="text-white font-medium mb-2">HR Excellence</h5>
              <ul className="text-sm text-slate-400 space-y-1">
                <li>• Competitive compensation attracts top talent</li>
                <li>• Regular training improves productivity</li>
                <li>• Clear career paths reduce turnover</li>
              </ul>
            </div>
            <div className="p-3 bg-white/5 rounded">
              <h5 className="text-white font-medium mb-2">TPRM Framework</h5>
              <ul className="text-sm text-slate-400 space-y-1">
                <li>• Risk-based vendor tiering</li>
                <li>• Continuous monitoring for critical vendors</li>
                <li>• Regular due diligence reviews</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-slate-900/30 border border-white/10 rounded-lg p-5">
          <h4 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
            <span>🎯</span> Compliance Goals
          </h4>
          <div className="space-y-3">
            <div className="flex justify-between items-center p-3 bg-white/5 rounded">
              <span className="text-slate-300">SOC 2 Type II</span>
              <span className={`font-medium ${stats.compliance >= 80 && orgMetrics.auditReadiness === 'Ready' ? 'text-emerald-400' : stats.compliance >= 60 ? 'text-yellow-400' : 'text-red-400'}`}>
                {stats.compliance >= 80 && orgMetrics.auditReadiness === 'Ready' ? 'Ready' : stats.compliance >= 60 ? 'In Progress' : 'Not Started'}
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-white/5 rounded">
              <span className="text-slate-300">PCI DSS</span>
              <span className={`font-medium ${orgMetrics.counts[BuildingType.Firewall] ? 'text-emerald-400' : 'text-yellow-400'}`}>
                {orgMetrics.counts[BuildingType.Firewall] ? 'Compliant' : 'Needs Firewall'}
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-white/5 rounded">
              <span className="text-slate-300">GDPR</span>
              <span className={`font-medium ${orgMetrics.counts[BuildingType.GDPROffice] ? 'text-emerald-400' : 'text-slate-500'}`}>
                {orgMetrics.counts[BuildingType.GDPROffice] ? 'Compliant' : 'Not Implemented'}
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-white/5 rounded">
              <span className="text-slate-300">Internal Audit</span>
              <span className={`font-medium ${orgMetrics.auditReadiness === 'Ready' ? 'text-cyan-400' : 'text-yellow-400'}`}>
                {orgMetrics.auditReadiness === 'Ready' ? 'Scheduled' : 'Setup Required'}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrgManagementTab;
